import type { CSSProperties, ReactNode } from 'react';
import { Fragment, forwardRef } from 'react';
import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import type { UnitProps } from './components';
import { Separator, Tooltip, Unit } from './components';
import { DEFAULT_SIZE_VALUE, defaultUnitLabelTexts } from './default-settings';
import { useCountdownTimer, useFormatTimer } from './hooks';
import type {
  CountdownFinishHandler,
  TimerAPI,
  TimerAppearance
} from './types';

export type TimerProps = Partial<TimerAppearance> & {
  /** Value in milliseconds */
  value: number;
  style?: CSSProperties;
  className?: string;
  isPaused?: boolean;
  editMode?: boolean;
  onCountdownFinish?: CountdownFinishHandler;
  onCountdownReset?: () => void;
  tooltipTitle?: ReactNode;
  withTooltip?: boolean;
};

export const Timer = forwardRef<TimerAPI, TimerProps>(
  (
    {
      style,
      className,
      value,
      onCountdownFinish,
      onCountdownReset,
      tooltipTitle,
      isPaused = false,
      timeFormat = 'auto',
      timerStyle = 'combined',
      size = DEFAULT_SIZE_VALUE,
      timerColor = '#111',
      timerBackgroundColor = '#fff',
      animation = 'none',
      showLabels = true,
      unitLabelTexts = defaultUnitLabelTexts,
      unitLabelColor = '#111',
      separatorColor = '#111',
      separatorStyle = 'colon',
      editMode = false,
      withTooltip = true
    },
    ref
  ) => {
    const format = useFormatTimer({ value, timeFormat, editMode });
    const time = useCountdownTimer({
      ref,
      value,
      format,
      editMode,
      isPaused,
      onCountdownFinish,
      onCountdownReset
    });
    const derivedWithTooltip = withTooltip && isPaused;

    return (
      <Tooltip title={tooltipTitle} disabled={!derivedWithTooltip}>
        <Container
          className={className}
          style={style}
          _isTooltipShowing={derivedWithTooltip}
          _size={size}
          _unitStyle={timerStyle}
          _hideSeparator={separatorStyle === 'none'}
        >
          {format.map((format, index, array) => {
            const hideSeparator =
              separatorStyle === 'none' || index === array.length - 1;

            return (
              <Fragment key={format}>
                <Unit
                  size={size}
                  unitStyle={timerStyle}
                  color={timerColor}
                  backgroundColor={timerBackgroundColor}
                  animation={animation}
                  showLabels={showLabels}
                  labelColor={unitLabelColor}
                  labelText={unitLabelTexts[format]}
                >
                  {time[format]}
                </Unit>

                <Show when={!hideSeparator}>
                  <Separator
                    size={size}
                    style={separatorStyle}
                    color={separatorColor}
                    unitStyle={timerStyle}
                    unitFormat={format}
                    showLabels={showLabels}
                  />
                </Show>
              </Fragment>
            );
          })}
        </Container>
      </Tooltip>
    );
  }
);

const unitGapMapping: Record<NonNullable<UnitProps['unitStyle']>, number> = {
  clean: 5 / DEFAULT_SIZE_VALUE,
  separated: 15 / DEFAULT_SIZE_VALUE,
  combined: 15 / DEFAULT_SIZE_VALUE,
  gradient: 15 / DEFAULT_SIZE_VALUE,
  outline: 15 / DEFAULT_SIZE_VALUE
};

const Container = styled.div.attrs<{
  _isTooltipShowing: boolean;
  _size: number;
  _unitStyle: NonNullable<UnitProps['unitStyle']>;
  _hideSeparator: boolean;
}>({
  className: createReadableClass({
    component: 'timer',
    element: 'container'
  })
})`
  display: flex;
  gap: ${({ _size, _unitStyle, _hideSeparator }) =>
    _hideSeparator && `${Math.round(unitGapMapping[_unitStyle] * _size)}px`};
  ${({ _isTooltipShowing }) => _isTooltipShowing && `cursor: default;`}
`;
